import { Howl, Howler } from 'howler';
import { SOUND_CONFIG, DEFAULT_SOUND_CONFIG, SOUND_NAMES, type SoundName, type SoundConfig } from '@/config/soundConfig';
export { SOUND_NAMES } from '@/config/soundConfig';

export class SoundManager {
    private readonly sounds: Map<string, Howl> = new Map();
    private readonly loading: Set<string> = new Set();
    private isEnabled = true;
    private globalVolume = 1;

    private loadSound(soundName: string, config: SoundConfig): Howl {
        const sound = new Howl({
            src: [`/sounds/${config.fileName}`],
            volume: (config.volume ?? 0.7) * this.globalVolume,
            loop: config.loop ?? false,
            preload: true,
        });
        this.sounds.set(soundName, sound);
        this.loading.delete(soundName);
        return sound;
    }

    // Returns cached Howl or triggers load; returns null while loading
    private getOrLoad(soundName: string): Howl | null {
        const cached = this.sounds.get(soundName);
        if (cached) return cached;
        if (this.loading.has(soundName)) return null;
        const config = SOUND_CONFIG[soundName] ?? DEFAULT_SOUND_CONFIG;
        this.loading.add(soundName);
        return this.loadSound(soundName, config);
    }

    public playSound(soundName: SoundName): void {
        if (!this.isEnabled) return;
        const sound = this.getOrLoad(soundName);
        if (sound) sound.play();
    }

    public playClickSound(): void {
        this.playSound(SOUND_NAMES.CLICK);
    }

    public stopSound(soundName: SoundName): void {
        this.sounds.get(soundName)?.stop();
    }

    public stopAllSounds(): void {
        this.sounds.forEach(s => s.stop());
    }

    public setGlobalVolume(volume: number): void {
        this.globalVolume = Math.max(0, Math.min(1, volume));
        Howler.volume(this.globalVolume);
        this.sounds.forEach((sound, name) => {
            const config = SOUND_CONFIG[name] ?? DEFAULT_SOUND_CONFIG;
            sound.volume((config.volume ?? 0.7) * this.globalVolume);
        });
    }

    public getGlobalVolume(): number {
        return this.globalVolume;
    }

    public setEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
        if (!enabled) this.stopAllSounds();
    }

    public isSoundEnabled(): boolean {
        return this.isEnabled;
    }

    public addSound(soundName: string, config: SoundConfig): void {
        this.loadSound(soundName, config);
    }

    public removeSound(soundName: string): void {
        const sound = this.sounds.get(soundName);
        if (sound) {
            sound.unload();
            this.sounds.delete(soundName);
        }
    }

    public getLoadedSounds(): string[] {
        return Array.from(this.sounds.keys());
    }

    public isSoundLoaded(soundName: string): boolean {
        return this.sounds.has(soundName);
    }
}

const soundManager = new SoundManager();

export const playSound = (soundName: SoundName): void => soundManager.playSound(soundName);
export const playClickSound = (): void => soundManager.playClickSound();
export const stopSound = (soundName: SoundName): void => soundManager.stopSound(soundName);
export const stopAllSounds = (): void => soundManager.stopAllSounds();
export const setGlobalVolume = (volume: number): void => soundManager.setGlobalVolume(volume);
export const getGlobalVolume = (): number => soundManager.getGlobalVolume();
export const setSoundEnabled = (enabled: boolean): void => soundManager.setEnabled(enabled);
export const isSoundEnabled = (): boolean => soundManager.isSoundEnabled();
export { soundManager };
